import { forwardRef, useState, InputHTMLAttributes, HTMLInputTypeAttribute } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';

const InputWrapper = styled.div`
  position: relative;
`;

const InputElement = styled.input(({ theme }) => {
  const { color, type } = theme;

  return `
    border-radius: 8px;
    border: 1px solid ${color.grayscale[600]};
    font-size: ${type.size.small};
    width: 100%;
    padding: 1rem;
    text-align: left;

    &.error {
      border-color: ${color.primary.regular};
    }

    &:disabled {
      background-color: ${color.grayscale[800]} !important;

      &:-webkit-autofill,
      &:-webkit-autofill:hover, 
      &:-webkit-autofill:focus, 
      &:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px ${color.grayscale[800]} inset !important;
      }
    }
  `;
});

const Label = styled.label(({ theme }) => {
  const { color, type } = theme;

  return `
    font-size: ${type.size.regular};
    color: ${color.grayscale[100]};
    margin: 0 0 0.75rem;
    line-height: 1.1875rem;

    &.error {
      color: ${color.primary.regular};
    }
  `;
});

const ErrorMessage = styled.span(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.primary.regular};
    font-size: ${type.size.small};
    font-style: italic;
  `;
});

const VisibilityToggle = styled.button(({ theme }) => {
  const { color, type } = theme;

  return `
    color: ${color.grayscale[0]};
    width: ${type.size.larger};
    height: ${type.size.larger};
    right: ${type.size.tiny};
    top: 50%;
    transform: translateY(-50%);
    position: absolute;
  `;
});

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  invalid?: boolean | undefined;
  errors?: string | string[];
  hideOptional?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { label, id, name, invalid, errors, required, hideOptional, disabled, type, ...rest } = props;
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

  const inputType = (): HTMLInputTypeAttribute | undefined => {
    if ((type === 'password' && !passwordVisible) || (type === 'password' && props.disabled)) {
      return 'password';
    }

    if (type === 'password' && passwordVisible) {
      return 'text';
    }

    return type || 'text';
  };

  const parsedErrors =
    typeof errors === 'string'
      ? errors
      : errors?.map((error, key) => {
          return <div key={`${name}-error-${key}`}>{error}</div>;
        });

  return (
    <div>
      <Label htmlFor={id} className={invalid ? 'error' : ''}>
        {label} {!required && !hideOptional ? '(Optional)' : ''}
      </Label>

      <InputWrapper>
        <InputElement
          id={id}
          name={name}
          ref={ref}
          className={invalid ? 'error' : ''}
          aria-describedby={invalid ? `${id}-error` : ''}
          type={inputType()}
          disabled={disabled}
          {...rest}
        />

        {type === 'password' && !props.disabled && (
          <VisibilityToggle
            type="button"
            title={passwordVisible ? 'Hide password' : 'Show password'}
            onClick={() => setPasswordVisible(!passwordVisible)}
          >
            {passwordVisible ? <FontAwesomeIcon icon={faEyeSlash} /> : <FontAwesomeIcon icon={faEye} />}
          </VisibilityToggle>
        )}
      </InputWrapper>

      {invalid && <ErrorMessage id={`${id}-error`}>{parsedErrors}</ErrorMessage>}
    </div>
  );
});

export default Input;
